import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Edit3, Trash2, CornerDownRight, MoreHorizontal, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const MAX_LEN = 2000;

/**
 * Editorial comment drawer (Phase G3).
 *
 * Slides in from the right of the lightbox on desktop; bottom sheet on mobile.
 * Each row displays display_name + role pill, relative timestamp, body,
 * and an action menu (Edit if author, Delete if author or primary).
 *
 * Replies use parent_comment_id and visually indent once (flat-ish thread).
 * Soft-deleted rows render "— [name] removed their comment —" with name
 * preserved so the conversation stays readable.
 */
function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function RolePill({ role }) {
  return role === "primary" ? (
    <span className="px-1.5 py-0.5 text-[9px] tracking-[0.22em] uppercase bg-[#C9A961] text-stone-900 rounded-sm">primary</span>
  ) : (
    <span className="px-1.5 py-0.5 text-[9px] tracking-[0.22em] uppercase bg-[#F5EFE0] text-stone-700 rounded-sm">collaborator</span>
  );
}

function CommentRow({ comment, me, isPrimary, depth, onReply, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body || "");

  const isAuthor = me?.id === comment.author_collaborator_id;
  const canEdit = isAuthor && !comment.deleted;
  const canDelete = (isAuthor || isPrimary) && !comment.deleted;

  async function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed) return toast.error("Comment can't be empty");
    if (trimmed.length > MAX_LEN) return toast.error(`Max ${MAX_LEN} characters`);
    setEditing(false);
    await onEdit(comment.id, trimmed);
  }

  if (comment.deleted) {
    return (
      <div
        className={`py-2 text-xs italic text-stone-400 ${depth ? "ml-7 border-l border-stone-200 pl-3" : ""}`}
        data-testid={`comment-${comment.id}`}
      >
        — {comment.author_display_name} removed their comment —
      </div>
    );
  }

  return (
    <div
      className={`group py-3 ${depth ? "ml-7 border-l border-stone-200 pl-3" : ""}`}
      data-testid={`comment-${comment.id}`}
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-medium text-sm text-stone-900">{comment.author_display_name}</span>
        <RolePill role={comment.author_role} />
        <span className="text-[10px] text-stone-400 tracking-wide">{relTime(comment.created_at)}{comment.edited_at && " · edited"}</span>
        <div className="ml-auto relative">
          {(canEdit || canDelete) && !editing && (
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="p-1 text-stone-400 hover:text-stone-900 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`comment-menu-${comment.id}`}
              aria-label="Comment actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          )}
          {menuOpen && (
            <div className="absolute right-0 top-7 z-10 bg-white border border-stone-200 shadow-sm min-w-[120px]">
              {canEdit && (
                <button
                  onClick={() => { setEditing(true); setMenuOpen(false); setDraft(comment.body); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 flex items-center gap-2"
                  data-testid={`comment-edit-${comment.id}`}
                >
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => { setMenuOpen(false); onDelete(comment.id); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 flex items-center gap-2 text-red-700"
                  data-testid={`comment-delete-${comment.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full bg-stone-50 border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-stone-600 resize-none"
            rows={2}
            maxLength={MAX_LEN}
            autoFocus
            data-testid={`comment-edit-input-${comment.id}`}
          />
          <div className="flex items-center gap-2 mt-1">
            <button onClick={saveEdit} className="inline-flex items-center gap-1 px-3 h-7 bg-stone-900 text-[#F5EFE0] text-[10px] uppercase tracking-[0.22em]" data-testid={`comment-save-${comment.id}`}>
              <Check className="w-3 h-3" /> Save
            </button>
            <button onClick={() => setEditing(false)} className="text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{comment.body}</p>
          {depth === 0 && (
            <button
              onClick={() => onReply(comment.id, comment.author_display_name)}
              className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`comment-reply-${comment.id}`}
            >
              <CornerDownRight className="w-3 h-3" /> Reply
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function CommentDrawer({ photo, me, open, onClose, hook }) {
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);   // { id, name } | null
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);
  const scrollRef = useRef(null);

  const thread = hook.threads[photo?.id] || [];
  const loading = hook.loadingPhotoId === photo?.id;
  const isPrimary = me?.role === "primary";

  // Load + revalidate when the drawer opens
  useEffect(() => {
    if (open && photo?.id) hook.loadThread(photo.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, photo?.id]);

  // Auto-grow textarea
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(160, el.scrollHeight)}px`;
  }, [body]);

  // Scroll to bottom on new comment
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length]);

  async function submit(e) {
    e?.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    if (trimmed.length > MAX_LEN) return toast.error(`Max ${MAX_LEN} characters`);
    setBusy(true);
    const result = await hook.postComment(photo.id, trimmed, replyTo?.id || null, {
      collaboratorId: me?.id,
      displayName: me?.display_name,
      role: me?.role,
    });
    setBusy(false);
    if (result.ok) {
      setBody("");
      setReplyTo(null);
    } else {
      toast.error(formatApiErrorDetail(result.error?.response?.data?.detail) || "Couldn't post comment");
    }
  }

  function handleKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  async function handleDelete(commentId) {
    if (!window.confirm("Delete this comment?")) return;
    const res = await hook.deleteComment(photo.id, commentId);
    if (!res.ok) toast.error(formatApiErrorDetail(res.error?.response?.data?.detail) || "Couldn't delete");
  }

  async function handleEdit(commentId, newBody) {
    const res = await hook.editComment(photo.id, commentId, newBody);
    if (!res.ok) toast.error(formatApiErrorDetail(res.error?.response?.data?.detail) || "Couldn't edit");
  }

  // Build a parent-grouped list: root rows + nested replies in order
  const rootRows = thread.filter((c) => !c.parent_comment_id);
  const childrenByParent = thread.reduce((acc, c) => {
    if (c.parent_comment_id) (acc[c.parent_comment_id] = acc[c.parent_comment_id] || []).push(c);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — clicks close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-stone-900/40 backdrop-blur-[2px]"
            data-testid="comment-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 z-[81] h-full w-full sm:w-[380px] bg-[#FAF9F6] shadow-2xl flex flex-col"
            data-testid="comment-drawer"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
                <h3 className="font-serif text-lg text-stone-900 font-light tracking-tight">Comments</h3>
                {thread.filter((c) => !c.deleted).length > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">
                    {thread.filter((c) => !c.deleted).length}
                  </span>
                )}
              </div>
              <button onClick={onClose} className="p-1 text-stone-500 hover:text-stone-900" data-testid="comment-drawer-close" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-stone-100">
              {loading && thread.length === 0 && (
                <div className="py-10 text-center text-xs uppercase tracking-[0.22em] text-stone-400">Loading…</div>
              )}
              {!loading && thread.length === 0 && (
                <div className="py-12 text-center" data-testid="comment-empty-state">
                  <p className="font-serif italic text-stone-500 text-base leading-relaxed">
                    Be the first to say something<br />about this photo.
                  </p>
                </div>
              )}
              {rootRows.map((c) => (
                <div key={c.id}>
                  <CommentRow
                    comment={c}
                    me={me}
                    isPrimary={isPrimary}
                    depth={0}
                    onReply={(id, name) => { setReplyTo({ id, name }); taRef.current?.focus(); }}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                  {(childrenByParent[c.id] || []).map((child) => (
                    <CommentRow
                      key={child.id}
                      comment={child}
                      me={me}
                      isPrimary={isPrimary}
                      depth={1}
                      onReply={() => {}}     // no nested replies past depth 1
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              ))}
            </div>

            <form onSubmit={submit} className="border-t border-stone-200 px-5 py-3 bg-white" data-testid="comment-composer">
              {replyTo && (
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">
                  <span>Replying to <span className="text-stone-900 normal-case tracking-normal">{replyTo.name}</span></span>
                  <button type="button" onClick={() => setReplyTo(null)} className="hover:text-stone-900">Cancel</button>
                </div>
              )}
              <textarea
                ref={taRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Share a thought…"
                rows={2}
                maxLength={MAX_LEN}
                className="w-full bg-transparent text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none resize-none leading-relaxed"
                data-testid="comment-composer-input"
              />
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-[10px] text-stone-400">{body.length}/{MAX_LEN} · ⌘/Ctrl + Enter</span>
                <button
                  type="submit"
                  disabled={busy || !body.trim()}
                  className="inline-flex items-center gap-1.5 px-3 h-8 bg-stone-900 text-[#F5EFE0] text-[10px] uppercase tracking-[0.22em] disabled:opacity-40 hover:opacity-90"
                  data-testid="comment-composer-send"
                >
                  <Send className="w-3 h-3" /> Send
                </button>
              </div>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
